import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { AlertCircle } from 'lucide-react';
import { TimeframeSelect, DateRangeInputs, PREV_LABEL } from './TimeframeSelect';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const usePeriod = () => {
  const [timeframe, setTimeframe] = useState('today');
  const [range, setRange] = useState({ start: '', end: '' });
  return { timeframe, setTimeframe, range, setRange };
};

export const useChattingData = (endpoint, period, refreshKey) => {
  const { timeframe, range } = period;
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const ready = timeframe !== 'custom' || Boolean(range.start && range.end && range.start <= range.end);

  const load = useCallback(async () => {
    if (!ready) { setData(null); setError(null); setLoading(false); return; }
    try {
      const params = timeframe === 'custom' ? { timeframe, start_date: range.start, end_date: range.end } : { timeframe };
      const r = await axios.get(`${API_URL}/chatting/${endpoint}`, { params });
      setData(r.data);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load chatting stats');
    } finally {
      setLoading(false);
    }
  }, [endpoint, timeframe, range.start, range.end, ready, refreshKey]);

  useEffect(() => {
    setLoading(true);
    load();
    const t = setInterval(load, 300000);
    return () => clearInterval(t);
  }, [load]);

  return { data, error, loading };
};

export const ChattingShell = ({ title, subtitle, period, error, fetchedAt, stale, children }) => (
  <div className="space-y-8">
    <div className="flex flex-wrap items-center justify-between gap-4">
      <div>
        <h1 className="text-4xl md:text-5xl font-bold font-heading text-white tracking-tight" data-testid="dashboard-title">{title}</h1>
        <p className="text-base text-muted-foreground mt-2">{subtitle}</p>
      </div>
      <div className="flex items-center gap-3">
        {fetchedAt && (
          <span className={`text-xs ${stale ? 'text-amber-500' : 'text-zinc-500'}`} data-testid="chatting-fetched-at">
            {stale ? 'Cached · ' : 'Updated '}{new Date(fetchedAt).toLocaleTimeString()}
          </span>
        )}
        {period.timeframe === 'custom' && <DateRangeInputs range={period.range} onChange={period.setRange} />}
        <TimeframeSelect value={period.timeframe} onChange={period.setTimeframe} />
      </div>
    </div>
    {error ? (
      <div className="bg-amber-500/10 border border-amber-500/20 rounded-md p-4 flex gap-2 text-amber-400" data-testid="chatting-error">
        <AlertCircle className="w-5 h-5 shrink-0" /> {error}
      </div>
    ) : period.timeframe === 'custom' && !(period.range.start && period.range.end) ? (
      <div className="bg-card p-6 rounded-md border border-border text-zinc-400" data-testid="custom-range-hint">
        Pick a start date and an end date to see the stats.
      </div>
    ) : children}
  </div>
);

export { PREV_LABEL };
